import { useCallback, useEffect, useRef, useState } from 'react';
import type { Card, CardResult, Language } from '../../../../shared/types';
import { matchSpokenAnswer, type MatchVerdict } from './answerMatch';
import { describeStep, parseVoiceCommand, type HandsFreeMode } from './handsFreeSteps';
import type { Recognition } from './useRecognition';
import type { Speaker } from './useSpeaker';

/**
 * Drives a drill by voice alone.
 *
 * The whole design problem here is that a hands-free drill has no rescue. With
 * a keyboard, a stuck card is an annoyance you press through; with your hands
 * full it is the end of the session. So every wait has a ceiling, every engine
 * callback that might not fire has a timer behind it, and the loop always
 * advances even when everything underneath it has failed.
 *
 * The loop is written as one effect per card rather than a `while` loop over
 * the session, because an async loop closes over the state it started with and
 * would answer the second card against the first card's text. Keying the effect
 * on the card's id means the machinery is re-entered fresh each time the
 * session advances, and the cleanup cancels whatever was in flight.
 */

export type Phase = 'stopped' | 'prompting' | 'listening' | 'checking' | 'revealing' | 'finished';

export interface HandsFreeReport {
  /** What the engine settled on, for the transcript strip. */
  heard: string;
  verdict: MatchVerdict;
  /** The form the answer was judged against. */
  expected: string;
}

export interface HandsFree {
  active: boolean;
  phase: Phase;
  /** Live text from the microphone while listening. */
  interim: string;
  /** The judgement on the card just answered. */
  lastReport: HandsFreeReport | null;
  /** Set when the drill cannot run at all — no voice, no microphone. */
  problem: string | null;
  /** A passing message — "I didn't catch that" — rather than a verdict. */
  notice: string | null;
  start: () => void;
  stop: () => void;
  /** Re-asks the current card without marking it. */
  repeat: () => void;
  /** Marks the current card wrong and moves on. */
  skip: () => void;
  /** Corrects the matcher's last verdict. */
  override: (result: CardResult) => void;
}

interface SessionBridge {
  current: Card | null;
  done: boolean;
  answer: (result: CardResult) => void;
}

export interface HandsFreeOptions {
  mode: HandsFreeMode;
  language: Language;
  speaker: Speaker;
  recognition: Recognition;
  session: SessionBridge;
  /** How long to leave the microphone open for one answer. */
  listenMs: number;
  /** Speech rate, shared with the rest of the drill. */
  rate: number;
}

/** A cancellable pause. */
function wait(ms: number, signal: { cancelled: boolean }): Promise<void> {
  return new Promise((resolve) => {
    const id = setTimeout(resolve, ms);
    const check = setInterval(() => {
      if (signal.cancelled) {
        clearTimeout(id);
        clearInterval(check);
        resolve();
      }
    }, 80);
    setTimeout(() => clearInterval(check), ms + 100);
  });
}

export function useHandsFree({
  mode,
  language,
  speaker,
  recognition,
  session,
  listenMs,
  rate,
}: HandsFreeOptions): HandsFree {
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState<Phase>('stopped');
  const [lastReport, setLastReport] = useState<HandsFreeReport | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Bumped to re-run the current card without advancing the session. */
  const [attempt, setAttempt] = useState(0);
  /**
   * Consecutive windows that heard nothing at all.
   *
   * Silence is not a wrong answer — far more often it is a microphone that is
   * muted, taken by another tab, or pointed at the wrong device. Marking those
   * cards wrong would quietly wreck a review schedule while the learner stood
   * there talking to a machine that could not hear them, so a silent window
   * re-asks instead. Twice in a row is no longer bad luck, and the drill stops
   * and says what to check.
   */
  const silences = useRef(0);

  // The session's functions change identity every render. Held in a ref so the
  // per-card effect does not restart every time the queue is touched.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const card = session.current;
  const cardId = card?.id ?? null;

  const stop = useCallback(() => {
    setActive(false);
    setPhase('stopped');
    speaker.cancel();
    recognition.stop();
  }, [speaker, recognition]);

  const start = useCallback(() => {
    setProblem(null);
    setNotice(null);
    setLastReport(null);
    silences.current = 0;
    setActive(true);
  }, []);

  const repeat = useCallback(() => setAttempt((n) => n + 1), []);

  const skip = useCallback(() => {
    speaker.cancel();
    recognition.stop();
    sessionRef.current.answer('incorrect');
  }, [speaker, recognition]);

  const override = useCallback(
    (result: CardResult) => {
      setLastReport((prev) =>
        prev ? { ...prev, verdict: result === 'correct' ? 'match' : 'miss' } : prev,
      );
      // The session has already been told; this records the correction as a
      // fresh answer on the same card, which is what the learner means by
      // "I was right" — the next write wins.
      sessionRef.current.answer(result);
    },
    [],
  );

  useEffect(() => {
    if (!active) return;
    if (session.done) {
      setPhase('finished');
      return;
    }
    if (!card) return;

    const signal = { cancelled: false };
    const step = describeStep(mode, card, language);

    // Refuse rather than mispronounce: this is the same rule useSpeech holds.
    if (!speaker.voiceFor(step.promptLang)) {
      setProblem(
        `No ${step.promptLang.split('-')[0] === 'en' ? 'English' : language.name} voice is installed, so this mode cannot read the prompt aloud.`,
      );
      setActive(false);
      return;
    }

    (async () => {
      setPhase('prompting');
      await speaker.speak(step.promptText, step.promptLang, rate);
      if (signal.cancelled) return;

      // ── Echo: nothing to judge ──────────────────────────────────────────
      if (!step.graded) {
        // A beat to say it back, scaled to how much there is to say.
        await wait(Math.max(1200, step.promptText.length * 160), signal);
        if (signal.cancelled) return;

        setPhase('revealing');
        await speaker.speak(step.promptText, step.promptLang, Math.max(0.5, rate - 0.15));
        if (signal.cancelled) return;
        await speaker.speak(step.revealText, step.revealLang, rate);
        if (signal.cancelled) return;

        setLastReport({ heard: '', verdict: 'match', expected: step.expectedText });
        // Deliberately not recorded — see the note in HandsFreePanel. Advancing
        // without answering would stall the queue, so echo runs its own cursor
        // via `repeat` on the caller's side; here we simply move the session on
        // with the result the learner would have given themselves.
        sessionRef.current.answer('correct');
        return;
      }

      // ── Recall and production: listen, then judge ───────────────────────
      // A beat between the prompt ending and the microphone opening. Without
      // it the engine can catch the tail of its own voice, and Chrome is still
      // tearing down the audio output when start() lands.
      await wait(250, signal);
      if (signal.cancelled) return;

      setPhase('listening');
      const heard = await recognition.listen(step.listenLang, listenMs);
      if (signal.cancelled) return;

      setPhase('checking');

      if (!heard || heard.alternatives.length === 0) {
        silences.current += 1;
        if (silences.current >= 2) {
          setProblem(
            'Nothing was heard twice in a row. Check that the right microphone is selected and that no other tab is using it.',
          );
          setActive(false);
          return;
        }
        // Not an answer, so not marked. Ask again.
        setNotice("I didn't catch that — have another go.");
        setAttempt((n) => n + 1);
        return;
      }

      silences.current = 0;
      setNotice(null);

      // A steering word is not an answer, so it is checked first — and only
      // against the engine's best guess, so a card whose answer resembles a
      // command is not hijacked by a low-confidence alternative.
      const command = parseVoiceCommand(heard.alternatives[0]);
      if (command === 'stop') {
        stop();
        return;
      }
      if (command === 'repeat') {
        setAttempt((n) => n + 1);
        return;
      }
      if (command === 'skip') {
        sessionRef.current.answer('incorrect');
        return;
      }
      if (command === 'correct' || command === 'wrong') {
        sessionRef.current.answer(command === 'correct' ? 'correct' : 'incorrect');
        return;
      }

      // Every alternative gets a go; the engine's second guess is often right,
      // and accepting it costs nothing the learner did not actually say.
      let best = matchSpokenAnswer(heard.alternatives[0], step.expectedText, step.matchLanguage, {
        articlesMatter: step.articlesMatter,
      });
      for (const alternative of heard.alternatives.slice(1)) {
        const candidate = matchSpokenAnswer(alternative, step.expectedText, step.matchLanguage, {
          articlesMatter: step.articlesMatter,
        });
        const rank = { match: 2, close: 1, miss: 0 } as const;
        if (rank[candidate.verdict] > rank[best.verdict]) best = candidate;
      }

      setLastReport({
        heard: heard.alternatives[0],
        verdict: best.verdict,
        expected: best.closestTo,
      });

      // A clean hit says nothing — speaking "correct" after every card doubles
      // the length of a session and teaches nothing. Anything less than clean
      // hears the right form said properly, which is the part worth having.
      if (best.verdict !== 'match') {
        setPhase('revealing');
        await speaker.speak(step.revealText, step.revealLang, rate);
        if (signal.cancelled) return;
      }

      sessionRef.current.answer(best.verdict === 'miss' ? 'incorrect' : 'correct');
    })();

    return () => {
      signal.cancelled = true;
      speaker.cancel();
      recognition.stop();
    };
    // `session.done` and `card.id` are the real drivers; the session's methods
    // live in a ref precisely so they cannot restart this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, cardId, attempt, mode, language, listenMs, rate, session.done]);

  // Surfacing a refused microphone is the difference between "it is broken" and
  // "click the padlock in the address bar".
  useEffect(() => {
    if (recognition.error === 'not-allowed') {
      setProblem('The microphone is blocked. Allow it in the address bar, then start again.');
      setActive(false);
    } else if (recognition.error === 'local-unavailable') {
      setProblem(
        'On-device recognition is not available in this browser, and local-only is switched on.',
      );
      setActive(false);
    }
  }, [recognition.error]);

  return {
    active,
    phase: active ? phase : session.done ? 'finished' : 'stopped',
    interim: recognition.interim,
    lastReport,
    problem,
    notice,
    start,
    stop,
    repeat,
    skip,
    override,
  };
}
